import React, { useState, useEffect } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import { Ticket, Phone, Megaphone, ClipboardText, Target, Export, ArrowsClockwise, Lightbulb, ArrowRight } from '@phosphor-icons/react';
import { API_CONFIG, authFetch } from '../config';
import SubPageHeader from '../components/SubPageHeader';

interface StepStatus {
  lotteriesCount: number;
  callcentersCount: number;
  werbewegeCount: number;
  campaignsCount: number;
  actionsCount: number;
}

export default function WorkflowGuide() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [status, setStatus] = useState<StepStatus>({
    lotteriesCount: 0,
    callcentersCount: 0,
    werbewegeCount: 0,
    campaignsCount: 0,
    actionsCount: 0
  });

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const [lotteries, callcenters, werbewege, campaigns, actions] = await Promise.all([
        authFetch(`${API_CONFIG.API_V1}/lotteries`).then(r => r.json()),
        authFetch(`${API_CONFIG.API_V1}/callcenters`).then(r => r.json()),
        authFetch(`${API_CONFIG.API_V1}/werbewege`).then(r => r.json()),
        authFetch(`${API_CONFIG.API_V1}/campaigns`).then(r => r.json()),
        authFetch(`${API_CONFIG.API_V1}/actions`).then(r => r.json())
      ]);

      setStatus({
        lotteriesCount: lotteries.length,
        callcentersCount: callcenters.length,
        werbewegeCount: werbewege.length,
        campaignsCount: campaigns.length,
        actionsCount: actions.length
      });
    } catch (error) {
      console.error('Failed to load status:', error);
    }
  };

  const steps = [
    {
      number: 1,
      title: 'Lotterien einrichten',
      description: 'Jede neue Lotterie (SKL/NKL) muss mit den relevanten Informationen eingerichtet werden.',
      count: status.lotteriesCount,
      link: '/campaigns?tab=lotteries',
      color: '#003366',
      icon: <Ticket size={isMobile ? 28 : 36} weight="duotone" />
    },
    {
      number: 2,
      title: 'Callcenter aktivieren',
      description: 'Callcenter werden je Lotterie aktiviert mit entsprechenden Abrechnungsinformationen.',
      count: status.callcentersCount,
      link: '/campaigns?tab=callcenters',
      color: '#0066cc',
      icon: <Phone size={isMobile ? 28 : 36} weight="duotone" />
    },
    {
      number: 3,
      title: 'Werbewege einrichten',
      description: 'Werbewege, Vermittler, Adresslisten oder Zeitschriften können hier oder direkt bei der Aktionseinrichtung angelegt werden.',
      count: status.werbewegeCount,
      link: '/campaigns?tab=werbewege',
      color: '#0099ff',
      icon: <Megaphone size={isMobile ? 28 : 36} weight="duotone" />
    },
    {
      number: 4,
      title: 'Kampagnen erstellen',
      description: 'Kampagnen bekommen ein Kürzel und abwicklungsrelevante Informationen. Kampagnen können zwischen Lotterien übernommen werden.',
      count: status.campaignsCount,
      link: '/campaigns?tab=campaigns',
      color: '#00ccff',
      icon: <ClipboardText size={isMobile ? 28 : 36} weight="duotone" />
    },
    {
      number: 5,
      title: 'Aktionen anlegen & WKZ vergeben',
      description: 'Jede Aktion wird einer Kampagne zugeordnet und bekommt eine WKZ (Werbekennzeichen). Automatisch werden auch Callcenter-WKZ\'s generiert.',
      count: status.actionsCount,
      link: '/campaigns?tab=actions',
      color: '#00ffcc',
      icon: <Target size={isMobile ? 28 : 36} weight="duotone" />
    },
    {
      number: 6,
      title: 'WKZ-Verwaltung & Export',
      description: 'Alle eingerichteten WKZ\'s mit ihren Informationen werden an die Lotterie-Software (MuB) geschickt.',
      count: null,
      link: '/campaigns?tab=campaigns',
      color: '#10b981',
      icon: <Export size={isMobile ? 28 : 36} weight="duotone" />
    }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <SubPageHeader title="Workflow & WKZ-Verwaltung" />

      {/* Page Title */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: isMobile ? '1rem' : '1.5rem 2rem'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <h2 style={{
            margin: '0 0 0.5rem 0',
            color: '#003366',
            fontSize: isMobile ? '1.3rem' : '1.5rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <ArrowsClockwise size={isMobile ? 24 : 28} weight="duotone" />
            Workflow & WKZ-Verwaltung
          </h2>
          <p style={{
            margin: 0,
            color: '#6b7280',
            fontSize: isMobile ? '0.9rem' : '1rem'
          }}>
            Grundsätzlicher Ablauf: Von der Lotterie-Einrichtung bis zur WKZ-Vergabe
          </p>
        </div>
      </div>

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '1rem' : '2rem'
      }}>
        {/* Overview Box */}
        <div style={{
          backgroundColor: '#e0f2fe',
          borderLeft: '4px solid #0284c7',
          padding: isMobile ? '1rem' : '1.5rem',
          borderRadius: '8px',
          marginBottom: isMobile ? '1.5rem' : '2rem'
        }}>
          <h3 style={{
            margin: '0 0 0.75rem 0',
            color: '#075985',
            fontSize: isMobile ? '1rem' : '1.1rem',
            fontWeight: '600'
          }}>
            Herkunftsmodul - Grundsätzlicher Ablauf
          </h3>
          <div style={{ fontSize: isMobile ? '0.9rem' : '0.95rem', color: '#0c4a6e', lineHeight: '1.6' }}>
            <p style={{ margin: '0 0 0.5rem 0' }}>
              <strong>Kampagnen</strong> erhalten ein Kürzel und abwicklungsrelevante Informationen.
            </p>
            <p style={{ margin: '0 0 0.5rem 0' }}>
              Jede <strong>Aktion</strong> wird einer Kampagne zugeordnet und bekommt eine <strong>WKZ (Werbekennzeichen)</strong> - die Aktions-WKZ.
            </p>
            <p style={{ margin: '0 0 0.5rem 0' }}>
              Diese wird ergänzt um eine <strong>Callcenter-WKZ</strong> (für Abrechnungszwecke).
            </p>
            <p style={{ margin: '0' }}>
              Für jede SKL bzw. NKL werden neue WKZ's vergeben. <strong>Controlling</strong> erfolgt auf drei Ebenen: Aktionsebene, Callcenterebene, Agentenebene.
            </p>
          </div>
        </div>

        {/* Workflow Steps */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
          gap: isMobile ? '1rem' : '1.5rem'
        }}>
          {steps.map((step, index) => (
            <Link
              key={step.number}
              to={step.link}
              style={{
                textDecoration: 'none',
                display: 'flex'
              }}
            >
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: isMobile ? '1.25rem' : '1.5rem',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '2px solid #e5e7eb',
                transition: 'all 0.2s',
                cursor: 'pointer',
                width: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = step.color;
                e.currentTarget.style.boxShadow = `0 4px 12px ${step.color}33`;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              >
                {/* Step Number Badge */}
                <div style={{
                  position: 'absolute',
                  top: isMobile ? '0.75rem' : '1rem',
                  right: isMobile ? '0.75rem' : '1rem',
                  width: isMobile ? '32px' : '40px',
                  height: isMobile ? '32px' : '40px',
                  borderRadius: '50%',
                  backgroundColor: step.color,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: isMobile ? '1rem' : '1.25rem'
                }}>
                  {step.number}
                </div>

                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  paddingRight: isMobile ? '2.5rem' : '3rem'
                }}>
                  {/* Icon & Title */}
                  <div>
                    <div style={{
                      color: step.color,
                      marginBottom: '0.5rem'
                    }}>
                      {step.icon}
                    </div>
                    <h3 style={{
                      margin: 0,
                      color: '#111827',
                      fontSize: isMobile ? '1rem' : '1.1rem',
                      fontWeight: '600'
                    }}>
                      {step.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: isMobile ? '0.85rem' : '0.9rem',
                    lineHeight: '1.5'
                  }}>
                    {step.description}
                  </p>

                  {/* Count Badge */}
                  {step.count !== null && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.75rem',
                      backgroundColor: `${step.color}15`,
                      borderRadius: '6px',
                      alignSelf: 'flex-start'
                    }}>
                      <span style={{
                        fontSize: isMobile ? '0.8rem' : '0.85rem',
                        color: step.color,
                        fontWeight: '500'
                      }}>
                        {step.count === 0 ? 'Noch keine Einträge' : `${step.count} ${step.count === 1 ? 'Eintrag' : 'Einträge'}`}
                      </span>
                    </div>
                  )}
                </div>

                {/* Arrow indicator */}
                <div style={{
                  position: 'absolute',
                  bottom: isMobile ? '0.75rem' : '1rem',
                  right: isMobile ? '0.75rem' : '1rem',
                  color: step.color
                }}>
                  <ArrowRight size={isMobile ? 24 : 28} weight="bold" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Info Box */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: isMobile ? '1.25rem' : '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          border: '2px solid #fef3c7',
          marginTop: isMobile ? '1.5rem' : '2rem'
        }}>
          <h3 style={{
            margin: '0 0 1rem 0',
            color: '#92400e',
            fontSize: isMobile ? '1rem' : '1.1rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Lightbulb size={isMobile ? 20 : 24} weight="duotone" />
            Wichtige Hinweise
          </h3>
          <ul style={{
            margin: 0,
            paddingLeft: '1.5rem',
            color: '#78350f',
            fontSize: isMobile ? '0.85rem' : '0.9rem',
            lineHeight: '1.8'
          }}>
            <li>Für jede SKL bzw. NKL werden neue WKZ's vergeben (5x im Jahr Wechsel NKL ↔ SKL)</li>
            <li>Jedes Werbemittel wird mit der entsprechenden WKZ versehen</li>
            <li>Callcenter-WKZ dient der Abrechnung (mehrere Callcenter auf gleichen Aktionen)</li>
            <li>Kampagnen können mit allen Informationen in die nächste SKL/NKL übernommen werden</li>
            <li>WKZ-Verwaltung sendet alle Daten an die Lotterie-Software (MuB)</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
